import { z } from 'zod';
import { isoDate, labelColor, name, richText, uuid } from './common.js';
import { ProjectRole } from './permissions.js';
// ───────────────────────────── Espacios y proyectos
export const workspaceSchema = z.object({ name: name(120), icon: z.string().max(60).optional() });
export const ProjectVisibility = { PRIVATE: 'PRIVATE', TEAM: 'TEAM' };
/** Tipo del núcleo. Los paquetes registran otros (spec §4.3). */
export const GENERAL_PROJECT_TYPE = 'GENERAL';
export const projectCreateSchema = z.object({
    name: name(160),
    description: richText.optional(),
    workspaceId: uuid.nullish(),
    typeCode: z.string().max(60).default(GENERAL_PROJECT_TYPE),
    visibility: z.enum(['PRIVATE', 'TEAM']).default('TEAM'),
    /** Índice de la paleta categórica (1–12). */
    color: labelColor.default(1),
    icon: z.string().max(60).default('mdi:folder-outline'),
    startDate: isoDate.nullish(),
    dueDate: isoDate.nullish(),
    templateId: uuid.optional(),
});
export const projectUpdateSchema = projectCreateSchema.omit({ templateId: true, typeCode: true }).partial().extend({
    ownerId: uuid.nullish(),
    /** Bloqueo opcional por límite WIP: si es true, no se puede pasar del límite. */
    enforceWip: z.boolean().optional(),
});
export const projectMemberSchema = z.object({ userId: uuid, role: z.nativeEnum(ProjectRole) });
// ───────────────────────────── Secciones
export const sectionCreateSchema = z.object({ name: name(120), wipLimit: z.number().int().min(1).max(999).nullish(), afterId: uuid.nullish() });
export const sectionUpdateSchema = z.object({ name: name(120).optional(), wipLimit: z.number().int().min(1).max(999).nullish(), collapsed: z.boolean().optional() });
export const moveSchema = z.object({ beforeId: uuid.nullish(), afterId: uuid.nullish() });
// ───────────────────────────── Tareas
export const Priority = { LOW: 'LOW', MEDIUM: 'MEDIUM', HIGH: 'HIGH', URGENT: 'URGENT' };
export const PRIORITY_LABEL = { LOW: 'Baja', MEDIUM: 'Media', HIGH: 'Alta', URGENT: 'Urgente' };
export const PRIORITY_ORDER = ['URGENT', 'HIGH', 'MEDIUM', 'LOW'];
export const RelationType = { BLOCKS: 'BLOCKS', DUPLICATES: 'DUPLICATES', RELATES: 'RELATES' };
export const RELATION_LABEL = {
    BLOCKS: ['Bloquea a', 'Bloqueada por'],
    DUPLICATES: ['Duplica a', 'Duplicada por'],
    RELATES: ['Relacionada con', 'Relacionada con'],
};
export const recurrenceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('DAILY'), every: z.number().int().min(1).max(365), businessDaysOnly: z.boolean().optional() }),
    z.object({ kind: z.literal('WEEKLY'), every: z.number().int().min(1).max(52), weekdays: z.array(z.number().int().min(0).max(6)).max(7), businessDaysOnly: z.boolean().optional() }),
    z.object({ kind: z.literal('MONTHLY'), every: z.number().int().min(1).max(12), dayOfMonth: z.number().int().min(1).max(31), businessDaysOnly: z.boolean().optional() }),
]);
const customFieldValues = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(z.string())]));
export const taskCreateSchema = z.object({
    projectId: uuid,
    sectionId: uuid.nullish(),
    parentId: uuid.nullish(),
    title: z.string().trim().min(1, 'Escribe un título').max(500),
    description: richText.optional(),
    assigneeId: uuid.nullish(),
    /** Equipo o dependencia a cargo; con o sin persona (sin ella, «sin repartir»). */
    teamId: uuid.nullish(),
    startDate: isoDate.nullish(),
    dueDate: isoDate.nullish(),
    /** Fecha límite expresada en días hábiles desde hoy (la API la convierte con el calendario del tenant). */
    dueInBusinessDays: z.number().int().min(0).max(3650).optional(),
    priority: z.nativeEnum(Priority).nullish(),
    labelIds: z.array(uuid).max(30).optional(),
    watcherIds: z.array(uuid).max(50).optional(),
    weight: z.number().min(0).max(1000).nullish(),
    effortMinutes: z.number().int().min(0).max(1_000_000).nullish(),
    requiresEvidence: z.boolean().optional(),
    recurrence: recurrenceSchema.nullish(),
    customFields: customFieldValues.optional(),
    /** Posición: después de esta tarea (en su sección). Sin ella, al final. */
    afterId: uuid.nullish(),
});
export const taskUpdateSchema = taskCreateSchema.omit({ projectId: true, afterId: true, dueInBusinessDays: true }).partial().extend({
    completed: z.boolean().optional(),
    focus: z.boolean().optional(),
});
export const taskMoveSchema = z.object({
    projectId: uuid.optional(),
    sectionId: uuid.nullish(),
    beforeId: uuid.nullish(),
    afterId: uuid.nullish(),
});
export const taskQuerySchema = z.object({
    projectId: uuid.optional(),
    portfolioId: uuid.optional(),
    sectionId: uuid.optional(),
    assigneeId: uuid.optional(),
    teamId: uuid.optional(),
    mine: z.coerce.boolean().optional(),
    /** Sin repartir en mis equipos (los que integro o dirijo, con sus subequipos). */
    teamInbox: z.coerce.boolean().optional(),
    watching: z.coerce.boolean().optional(),
    focus: z.coerce.boolean().optional(),
    completed: z.enum(['true', 'false', 'all']).default('all'),
    q: z.string().max(200).optional(),
    labelId: uuid.optional(),
    priority: z.nativeEnum(Priority).optional(),
    dueFrom: isoDate.optional(),
    dueTo: isoDate.optional(),
    overdue: z.coerce.boolean().optional(),
    parentId: uuid.optional(),
    includeSubtasks: z.coerce.boolean().optional(),
    limit: z.coerce.number().int().min(1).max(2000).default(500),
    cursor: z.string().optional(),
});
export const relationSchema = z.object({ type: z.nativeEnum(RelationType), targetTaskId: uuid });
export const watcherSchema = z.object({ userId: uuid });
// ───────────────────────────── Checklists, comentarios, etiquetas, adjuntos
export const checklistSchema = z.object({ title: name(200) });
export const checklistItemCreateSchema = z.object({ text: z.string().trim().min(1).max(500), afterId: uuid.nullish() });
export const checklistItemUpdateSchema = z.object({ text: z.string().trim().min(1).max(500).optional(), done: z.boolean().optional() });
export const commentSchema = z.object({ body: richText.refine((v) => v.replace(/<[^>]*>/g, '').trim().length > 0, 'Escribe un comentario') });
export const labelSchema = z.object({ name: name(60), color: labelColor });
export const ATTACHMENT_MAX_BYTES = 50 * 1024 * 1024;
export const ATTACHMENT_MAX_FILES = 10;
// ───────────────────────────── Campos personalizados
export const CustomFieldType = { TEXT: 'TEXT', NUMBER: 'NUMBER', SELECT: 'SELECT', DATE: 'DATE', PERSON: 'PERSON', CHECKBOX: 'CHECKBOX' };
export const customFieldSchema = z.object({
    name: name(80),
    type: z.nativeEnum(CustomFieldType),
    options: z.array(z.object({ value: z.string().max(60), label: z.string().max(80), color: labelColor.optional() })).max(100).optional(),
    /** project: de un proyecto; project_type: de un tipo; system: de un paquete (código inmutable). */
    scope: z.enum(['project', 'project_type']).default('project'),
    projectId: uuid.nullish(),
    projectType: z.string().max(60).nullish(),
});
// ───────────────────────────── Vistas
export const CORE_VIEW_TYPES = ['TABLE', 'BOARD', 'TIMELINE', 'CALENDAR', 'OVERVIEW', 'FILES', 'REPORTS'];
export const VIEW_TYPE_LABEL = { TABLE: 'Tabla', BOARD: 'Kanban', TIMELINE: 'Cronograma', CALENDAR: 'Calendario', OVERVIEW: 'Resumen', FILES: 'Archivos', REPORTS: 'Reportes' };
export const VIEW_TYPE_ICON = { TABLE: 'mdi:table', BOARD: 'mdi:view-column-outline', TIMELINE: 'mdi:chart-gantt', CALENDAR: 'mdi:calendar-month-outline', OVERVIEW: 'mdi:view-dashboard-outline', FILES: 'mdi:paperclip', REPORTS: 'mdi:chart-bar' };
/** Las que crea un proyecto nuevo sin plantilla (spec §3.1). */
export const DEFAULT_PROJECT_VIEWS = ['BOARD', 'TABLE', 'OVERVIEW', 'FILES'];
/** Campos por los que se puede agrupar el Kanban o la Tabla (además de los personalizados de tipo lista: `cf:<id>`). */
export const GROUPABLE_FIELDS = ['section', 'priority', 'assignee', 'team', 'label', 'completed'];
export const viewFilterSchema = z.object({
    field: z.string().max(80),
    operator: z.enum(['is', 'is_not', 'contains', 'before', 'after', 'empty', 'not_empty', 'in']),
    value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]).optional(),
});
export const viewConfigSchema = z.object({
    filters: z.array(viewFilterSchema).max(30).default([]),
    sort: z.array(z.object({ field: z.string().max(80), dir: z.enum(['asc', 'desc']) })).max(5).default([]),
    /** `section` por defecto; `priority`, `assignee`, `label`, `completed` o `cf:<id>`. */
    groupBy: z.string().max(80).nullish(),
    visibleFields: z.array(z.string().max(80)).max(60).optional(),
    cardColorBy: z.string().max(80).nullish(),
    showCompleted: z.boolean().default(true),
    timeline: z.object({ scale: z.enum(['day', 'week', 'month', 'quarter']).default('week'), showCriticalPath: z.boolean().default(false), showBaseline: z.boolean().default(false) }).optional(),
    calendar: z.object({ dateField: z.enum(['dueDate', 'startDate']).default('dueDate'), mode: z.enum(['month', 'week']).default('month') }).optional(),
});
export const viewCreateSchema = z.object({
    type: z.string().max(60),
    name: name(80),
    config: viewConfigSchema.default({}),
    /** true: solo la ve quien la crea. */
    personal: z.boolean().default(false),
});
export const viewUpdateSchema = z.object({ name: name(80).optional(), config: viewConfigSchema.optional(), hidden: z.boolean().optional(), isDefault: z.boolean().optional() });
// ───────────────────────────── Automatizaciones (acciones de sección)
export const AutomationTrigger = { TASK_CREATED_IN_SECTION: 'TASK_CREATED_IN_SECTION', TASK_MOVED_TO_SECTION: 'TASK_MOVED_TO_SECTION', TASK_COMPLETED: 'TASK_COMPLETED', TASK_OVERDUE: 'TASK_OVERDUE' };
export const AUTOMATION_TRIGGER_LABEL = {
    TASK_CREATED_IN_SECTION: 'Se crea una tarea en la sección',
    TASK_MOVED_TO_SECTION: 'Una tarea entra a la sección',
    TASK_COMPLETED: 'Se completa una tarea',
    TASK_OVERDUE: 'Una tarea se vence',
};
export const automationActionSchema = z.discriminatedUnion('type', [
    /** Con `teamId`, la tarea pasa a ese equipo (y `userId` puede ser null: sin repartir). */
    z.object({ type: z.literal('ASSIGN'), userId: uuid.nullable(), teamId: uuid.nullish() }),
    z.object({ type: z.literal('SET_DUE_IN_DAYS'), days: z.number().int().min(0).max(365), business: z.boolean().default(true) }),
    z.object({ type: z.literal('ADD_LABEL'), labelId: uuid }),
    z.object({ type: z.literal('REMOVE_LABEL'), labelId: uuid }),
    z.object({ type: z.literal('SET_PRIORITY'), priority: z.nativeEnum(Priority) }),
    z.object({ type: z.literal('ADD_CHECKLIST'), title: z.string().max(200), items: z.array(z.string().max(500)).max(50) }),
    z.object({ type: z.literal('ADD_WATCHER'), userId: uuid }),
    z.object({ type: z.literal('NOTIFY'), userIds: z.array(uuid).max(20), message: z.string().max(500) }),
    z.object({ type: z.literal('COMPLETE') }),
    z.object({ type: z.literal('MOVE_TO_SECTION'), sectionId: uuid }),
    z.object({ type: z.literal('START_TIMER') }),
    z.object({ type: z.literal('STOP_TIMER') }),
    /** Condición por decisión de Jev (spec §6.1-G): solo actúa en nivel AUTOMATIC con el valor esperado. */
    z.object({ type: z.literal('IF_DECISION'), schemaCode: z.string().max(60), field: z.string().max(60), equals: z.union([z.string(), z.boolean()]), then: z.array(z.lazy(() => automationActionSchema)).max(10) }),
]);
export const automationSchema = z.object({
    name: name(120),
    trigger: z.nativeEnum(AutomationTrigger),
    sectionId: uuid.nullish(),
    actions: z.array(automationActionSchema).min(1).max(20),
    active: z.boolean().default(true),
});
// ───────────────────────────── Tiempo
export const timeEntrySchema = z.object({
    minutes: z.number().int().min(1).max(24 * 60),
    date: isoDate,
    note: z.string().max(500).optional(),
});
// ───────────────────────────── Plantillas
export const templateFromProjectSchema = z.object({ projectId: uuid, name: name(120), description: z.string().max(500).optional(), includeTasks: z.boolean().default(true) });
// ───────────────────────────── Notificaciones
export const NotificationType = {
    TASK_ASSIGNED: 'TASK_ASSIGNED', MENTIONED: 'MENTIONED', COMMENTED: 'COMMENTED', TASK_DUE_SOON: 'TASK_DUE_SOON', TASK_OVERDUE: 'TASK_OVERDUE',
    TASK_UNBLOCKED: 'TASK_UNBLOCKED', TASK_COMPLETED: 'TASK_COMPLETED', AUTOMATION: 'AUTOMATION', REVIEW_SUGGESTION: 'REVIEW_SUGGESTION', REPORT_TO_REVIEW: 'REPORT_TO_REVIEW', REPORT_RETURNED: 'REPORT_RETURNED',
};
//# sourceMappingURL=work.js.map